import React, { useState } from "react";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import { downloadFile, useFetchData } from "../../../helpers/GlobalHelper";
import { GlobalConstants } from "../../constants/GlobalConstants";
import LmsTable from "../ui/LmsTable";

const getLoanSubstatusEndpoint = (loanId: string) => `/loans/sub-status-details-by-loan/${loanId}`;

export const DrawManagement = ({ loanId = "" }) => {
  const hasLoanId = !!loanId;
  const [{ data: loanData, isLoading: isLoanLoading, error: loanError }] = useFetchData(
    loanId ? `loans/${loanId}` : ""
  );

  const [{ data: substatusData, isLoading: isSubstatusLoading, error: substatusError }] =
    useFetchData(loanId ? getLoanSubstatusEndpoint(loanId) : "");

  const myForm = new BCFormController(
    {},
    {
      values:
        loanData && substatusData
          ? {
              loanId: loanData.id,
              directPay: substatusData.directPay,
              loanStatus: substatusData.loanPhaseLoanStatusDisplay,
              borrowerOrganizationId: loanData.borrowerOrganizationId,
            }
          : {},
    }
  );

  const [{ fetchData }] = useFetchData();
  const [isLoading, setIsLoading] = useState(false);
  const [loanPropertyId, setLoanPropertyId] = useState("");

  const addQueryToDrawManagementPath = (path: string, inputs): string => {
    const queryParams = {
      loanId: inputs.loanId,
      loanPropertyId: inputs.loanPropertyId,
      borrowerOrganizationId: inputs.borrowerOrganizationId,
    };

    return Object.keys(queryParams).reduce(
      (acc, queryKey) =>
        queryParams[queryKey] ? `${acc}&${queryKey}=${queryParams[queryKey]}` : acc,
      path + "?"
    );
  };

  const handleFetch = async (
    fetch: () => Promise<any>,
    onSuccess?: (data) => void,
    errorHandler?
  ): Promise<void> => {
    setIsLoading(true);
    try {
      const { data, error } = await fetch();
      if (error) {
        errorHandler(error);
      }
      onSuccess && onSuccess(data);
    } catch (e) {
      errorHandler(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadFile =
    (getEndpoint) =>
    async ({ inputs, setFormErrorMessage }) => {
      setIsLoading(true);
      try {
        await downloadFile(getEndpoint(inputs));
      } catch (e) {
        setFormErrorMessage(e);
      } finally {
        setIsLoading(false);
      }
    };

  const setFalsySubstatus = (setInputs) =>
    setInputs((prevInputs) => ({
      ...prevInputs,
      loanStatus: null,
      directPay: false,
    }));

  const handleSetSubstatus = ({ setInputs, setFormErrorMessage }, endpoint) => {
    setFalsySubstatus(setInputs);
    handleFetch(
      () => fetchData(endpoint),
      (data) => {
        setInputs((prevInputs) => ({
          ...prevInputs,
          directPay: data.directPay,
          loanStatus: data.loanPhaseLoanStatusDisplay,
        }));
      },
      setFormErrorMessage
    );
  };

  const onFormChanged = ({ lastUpdated, inputs, setInputs, setFormErrorMessage }) => {
    const bindedHandleSetsubstatus = handleSetSubstatus.bind(null, {
      setInputs,
      setFormErrorMessage,
    });

    const setSubstatusByLoan = () => {
      if (inputs.loanId) {
        bindedHandleSetsubstatus(getLoanSubstatusEndpoint(inputs.loanId));
      } else {
        setFalsySubstatus(setInputs);
      }
    };

    switch (lastUpdated) {
      case "loanId": {
        setSubstatusByLoan();
        break;
      }
      case "loanPropertyId": {
        if (inputs.loanPropertyId) {
          bindedHandleSetsubstatus(
            `/loans/sub-status-details-by-property/${inputs.loanPropertyId}`
          );
        } else {
          setSubstatusByLoan();
        }
        setLoanPropertyId(inputs.loanPropertyId || "");
        break;
      }
      case "borrowerOrganizationId": {
        setInputs((prevInputs) => ({
          borrowerOrganizationId: prevInputs.borrowerOrganizationId,
        }));
        break;
      }
    }
  };

  const handleSendToBorrowerClick = ({ setFormErrorMessage, inputs }) => {
    handleFetch(
      () => fetchData(addQueryToDrawManagementPath("/draws/send-to-borrower", inputs)),
      (data) => {
        if (data && Array.isArray(data.infoMessage) && data.infoMessage.length > 0) {
          setFormErrorMessage({
            message: data.infoMessage.reduce(
              (acc: string, msg: string): string => `${msg}\n${acc}`,
              ""
            ),
          });
        }
      },
      setFormErrorMessage
    );
  };

  myForm.addGroupFields([
    myForm.getDropdownField("Borrower", "/metadata/list-borrower-organizations", {
      name: "borrowerOrganizationId",
      disabled: hasLoanId,
    }),
    myForm.getDropdownField("Loan", "loans/list-borrower-loans?borrowerOrganizationId={id}", {
      dependencyField: "borrowerOrganizationId",
      disabled: hasLoanId,
    }),
    myForm.getDropdownField("Loan Property", "loandetails/list-loan-properties", {
      dependencyField: "loanId",
    }),
    myForm.getDisplayField("Loan Status", { isHiddenFunc: ({ inputs }) => !inputs.loanStatus }),
    myForm.getBlankField({ isHiddenFunc: ({ inputs }) => !!inputs.loanStatus }),
  ]);

  myForm.addCenteredGroup(
    [
      myForm.getButtonField(
        "Excel",
        handleDownloadFile((inputs) =>
          addQueryToDrawManagementPath("/draws/export-histories", inputs)
        ),
        {
          buttonIcon: GlobalConstants.Icons.EXCEL_OUTLINE,
        }
      ),
      myForm.getButtonField("Send To Borrower", handleSendToBorrowerClick),
      myForm.getButtonField(
        "Direct Pay Form",
        handleDownloadFile((inputs) => `/draws/direct-pay-form/${inputs.loanPropertyId}`),
        {
          isHiddenFunc: ({ inputs }) => !(inputs.directPay && inputs.loanPropertyId),
        }
      ),
    ],
    { isHiddenFunc: ({ inputs }) => !inputs.borrowerOrganizationId }
  );

  return (
    <>
      <LmsFormGenerator
        actions={[]}
        myForm={myForm}
        onFormChanged={onFormChanged}
        error={loanError || substatusError}
        isLoading={isLoading || isLoanLoading || isSubstatusLoading}
      />
      {loanPropertyId && <LmsTable apiSuffix={`/draws/histories/${loanPropertyId}`} />}
    </>
  );
};

export default DrawManagement;
